import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Go Higher
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
          Legal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3 leading-tight">
          Privacy Policy
        </h1>
        <p className="mt-4 text-white/50 text-sm">
          Last updated: February 1, 2026
        </p>

        <div className="mt-12 space-y-10 text-white/75 text-[15px] leading-relaxed">
          <Section title="1. Who We Are">
            <p>
              Go Higher, Inc. ("Go Higher," "we," "us," or "our") is an online
              reputation-management agency based in Louisiana, USA. This
              Privacy Policy explains how we collect, use, disclose, and
              protect personal information when you use igohigher.com, our
              client dashboard, or any of our services.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We collect the following categories of personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account information:</strong> name, email address,
                password (hashed), and — if you sign in with Google — your
                Google profile name, email, and profile picture URL.
              </li>
              <li>
                <strong>Business & case information:</strong> the business you
                enroll (name, Google Business Profile URL), the reviews you ask
                us to evaluate, and any related notes.
              </li>
              <li>
                <strong>Payment information:</strong> processed and stored by
                Stripe, Inc. We store only Stripe session IDs, invoice IDs,
                amounts, and payment status. We do not store full card numbers.
              </li>
              <li>
                <strong>Communications:</strong> messages you send us,
                including support requests and AI chatbot conversations with
                "Ella."
              </li>
              <li>
                <strong>Usage & device data:</strong> IP address, browser type,
                pages visited, and timestamps, collected via standard web
                analytics.
              </li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-2">
              <li>To deliver the Services you request.</li>
              <li>
                To process payments, send invoices, and manage subscriptions
                via Stripe.
              </li>
              <li>
                To communicate with you about your account, cases, and updates.
              </li>
              <li>To improve our website, services, and AI chatbot.</li>
              <li>To comply with legal obligations and enforce our Terms.</li>
            </ul>
          </Section>

          <Section title="4. Legal Bases (GDPR)">
            <p>
              For users in the European Economic Area or UK, we process
              personal data on the following legal bases: performance of a
              contract (delivering the Services); legitimate interests
              (security, fraud prevention, improving the Services); consent
              (marketing communications); and legal obligation.
            </p>
          </Section>

          <Section title="5. Third-Party Services">
            <p>
              We rely on a small set of trusted providers who process data on
              our behalf under strict contractual protections:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Stripe, Inc.</strong> — payment processing.
              </li>
              <li>
                <strong>Google LLC</strong> — Google Sign-In authentication and
                access to Google Business Profile data you authorize us to
                view.
              </li>
              <li>
                <strong>OpenAI, LLC</strong> — powers our AI assistant "Ella."
                Chat transcripts are used to answer your question and improve
                the assistant.
              </li>
              <li>
                <strong>Cal.com, Inc.</strong> — call scheduling.
              </li>
              <li>
                <strong>MongoDB, Inc.</strong> — application database.
              </li>
            </ul>
            <p>
              We do not sell your personal information to third parties.
            </p>
          </Section>

          <Section title="6. Cookies">
            <p>
              We use essential cookies to keep you signed in and to remember
              your preferences. Analytics cookies help us understand how the
              site is used. You can disable non-essential cookies via your
              browser settings.
            </p>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain account data for as long as your account is active and
              for up to 7 years thereafter for tax and legal compliance. You
              may request deletion at any time by emailing
              info@igohigher.com; we will delete data not required by law to
              retain.
            </p>
          </Section>

          <Section title="8. Security">
            <p>
              We use industry-standard security: HTTPS everywhere, bcrypt
              password hashing, encrypted database connections, and least-
              privilege access controls. No system is 100% secure — please use
              a strong, unique password.
            </p>
          </Section>

          <Section title="9. Your Rights">
            <p>
              Depending on your location, you may have the right to access,
              correct, delete, port, or restrict processing of your personal
              data, and to withdraw consent. To exercise any of these rights,
              email{" "}
              <a
                href="mailto:info@igohigher.com"
                className="text-[#10B981] hover:text-[#0ea774] underline underline-offset-4"
              >
                info@igohigher.com
              </a>{" "}
              from the email address on your account. Residents of California
              (CCPA/CPRA), the EEA (GDPR), and the UK have additional rights
              which we honor.
            </p>
          </Section>

          <Section title="10. Children">
            <p>
              Our Services are intended for business owners and are not
              directed at anyone under 18. We do not knowingly collect
              information from children.
            </p>
          </Section>

          <Section title="11. International Transfers">
            <p>
              Go Higher is based in the United States. If you access the
              Services from outside the US, your information will be
              transferred to and processed in the US, which may have different
              data-protection laws than your jurisdiction.
            </p>
          </Section>

          <Section title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Material
              changes will be notified via email or a notice in the dashboard.
              The "Last updated" date at the top of this page reflects the
              latest revision.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              For privacy questions or requests, email{" "}
              <a
                href="mailto:info@igohigher.com"
                className="text-[#10B981] hover:text-[#0ea774] underline underline-offset-4"
              >
                info@igohigher.com
              </a>{" "}
              or write to: Go Higher, Inc., New Orleans, LA, USA.
            </p>
          </Section>
        </div>
      </main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/40 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Go Higher, Inc.</div>
          <Link to="/terms" className="hover:text-white transition-colors">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold text-white">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}
